import { ReactElement } from 'react'
import { useIntl } from 'react-intl'
import { Box, Chip, styled, useTheme } from '@mui/material'
import { useBranding } from 'features/branding/hooks/brandingHooks'
import { Workflow, WorkflowListItem } from 'features/workflows/types'

interface WorkflowFeatureBadgeProps {
  workflow: Workflow | WorkflowListItem
  compact?: boolean
  alert?: boolean
  conditionCheck: (workflow: Workflow | WorkflowListItem) => boolean
  icon: ReactElement
  labelId: string
  onClick?: (workflowId: string) => void
  zIndex?: number
}

const Container = styled(Box)(() => ({
  position: 'relative',
  display: 'inline-flex'
}))

const CircleBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'customColor'
})<{ customColor: boolean }>(({ theme, customColor }) => ({
  width: theme.spacing(3.25),
  height: theme.spacing(3.25),
  borderRadius: '50%',
  backgroundColor: customColor ? theme.palette.grey[100] : theme.palette.primary.main,
  border: `1px solid ${theme.palette.common.white}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(-0.75),
  '& svg': {
    width: theme.spacing(2.25),
    height: theme.spacing(2.25),
    color: customColor ? theme.palette.text.primary : theme.palette.common.white
  }
}))

const Badge = styled(Chip)(({ theme }) => ({
  marginRight: theme.spacing(0.5)
}))

const GreenCircle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(-0.25),
  right: theme.spacing(-0.25),
  width: theme.spacing(1.25),
  height: theme.spacing(1.25),
  borderRadius: '50%',
  backgroundColor: theme.palette.secondary.main,
  border: `1px solid ${theme.palette.brand.navyBlue}`
}))

function WorkflowFeatureBadge({
  workflow,
  compact = false,
  alert = false,
  conditionCheck,
  icon,
  labelId,
  onClick,
  zIndex = 0
}: WorkflowFeatureBadgeProps) {
  const intl = useIntl()
  const theme = useTheme()
  const { data: branding } = useBranding()
  const isUsingCustomColor =
    branding.usePrimaryColorsForAppBar || branding.appBarBackgroundColor !== theme.palette.brand.navyBlue

  if (!conditionCheck(workflow)) {
    return null
  }

  if (compact) {
    return (
      <CircleBadge customColor={isUsingCustomColor} zIndex={zIndex}>
        {icon}
      </CircleBadge>
    )
  }

  return (
    <Container>
      <Badge
        onClick={onClick ? () => onClick(workflow.id) : undefined}
        size="small"
        icon={icon}
        label={intl.formatMessage({ id: labelId })}
        color={isUsingCustomColor ? 'default' : 'primary'}
      />
      {alert && <GreenCircle />}
    </Container>
  )
}

export default WorkflowFeatureBadge
